import { supabase } from './supabaseClient';
import { RegistrationFormData, LoginFormData } from '../lib/validations/auth';
import { AuthUser } from '../types/auth';
import { sendRegistrationEmail } from './api/email';


// Registration functions
export async function submitRegistrationRequest(data: RegistrationFormData): Promise<{ success: boolean; error?: string }> {
  try {
    // First, create the auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
    });

    if (authError) {
      console.error('Auth registration error:', authError);
      return { success: false, error: 'Грешка при създаване на акаунт' };
    }

    if (!authData.user) {
      return { success: false, error: 'Неуспешно създаване на потребител' };
    }

    // Then create the profile manually
    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: authData.user.id,
        role: data.role === 'CLIENT' ? 'CLIENT' : (data.providerType === 'AGENCY' ? 'AGENCY' : 'FREELANCER'),
        status: 'PENDING_VERIFICATION',
        full_name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        phone: data.phone,
        company: data.companyName,
        company_bulstat: data.companyBulstat,
        skills: data.skills || [],
        bio: data.bio,
        portfolio_url: data.portfolioUrl,
        hourly_rate: data.hourlyRate,
      });

    if (profileError) {
      console.error('Profile creation error:', profileError);
      return { success: false, error: 'Грешка при създаване на профил' };
    }

    // Give 6 free credits to new users
    const { error: creditsError } = await supabase
      .from('user_credits')
      .insert({
        user_id: authData.user.id,
        credits_balance: 6,
      });

    if (creditsError) {
      console.error('Credits creation error:', creditsError);
      // Don't fail registration if credits fail
    }

    // Send registration success email
    try {
      await sendRegistrationEmail(
        data.email,
        `${data.firstName} ${data.lastName}`,
        data.role === 'CLIENT' ? 'CLIENT' : (data.providerType === 'AGENCY' ? 'AGENCY' : 'FREELANCER')
      );
    } catch (emailError) {
      console.error('Email sending error:', emailError);
      // Don't fail registration if email sending fails
    }

    return { success: true };
  } catch (error) {
    console.error('Registration error:', error);
    return { success: false, error: 'Неочаквана грешка' };
  }
}

// Login functions
export async function login(credentials: LoginFormData): Promise<{ success: boolean; user?: AuthUser; error?: string }> {
  try {
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    if (authError) {
      return { success: false, error: 'Невалидни данни за вход' };
    }

    if (!authData.user) {
      return { success: false, error: 'Потребителят не е намерен' };
    }

    // Get user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authData.user.id)
      .single();

    if (profileError || !profile) {
      return { success: false, error: 'Профилът не е намерен' };
    }

    // Check if user is pending verification
    if (profile.status === 'PENDING_VERIFICATION') {
      return { 
        success: false, 
        error: 'Вашият акаунт очаква одобрение от администратор. Ще получите имейл, когато бъде одобрен.' 
      };
    }

    const user: AuthUser = {
      id: authData.user.id,
      email: authData.user.email!,
      role: profile.role,
      profile: {
        id: profile.id,
        role: profile.role,
        status: profile.status,
        firstName: profile.full_name.split(' ')[0] || '',
        lastName: profile.full_name.split(' ').slice(1).join(' ') || '',
        email: profile.email,
        phone: profile.phone,
        companyName: profile.company,
        companyBulstat: profile.company_bulstat,
        skills: profile.skills,
        bio: profile.bio, // Using bio field for bio
        portfolioUrl: profile.portfolio_url,
        hourlyRate: profile.hourly_rate,
        createdAt: profile.created_at,
        updatedAt: profile.updated_at,
      },
    };

    return { success: true, user };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, error: 'Неочаквана грешка при влизане' };
  }
}

// Logout function
export async function logout(): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      return { success: false, error: 'Грешка при излизане' };
    }

    return { success: true };
  } catch (error) {
    console.error('Logout error:', error);
    return { success: false, error: 'Неочаквана грешка при излизане' };
  }
}

// Get current user
export async function getCurrentUser(): Promise<{ success: boolean; user?: AuthUser; error?: string }> {
  try {
    const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();

    if (authError || !authUser) {
      return { success: false, error: 'Не сте влезли в системата' };
    }

    // Get user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authUser.id)
      .single();

    if (profileError || !profile) {
      return { success: false, error: 'Профилът не е намерен' };
    }

    // Check if user is pending verification
    if (profile.status === 'PENDING_VERIFICATION') {
      return { 
        success: false, 
        error: 'Вашият акаунт очаква одобрение от администратор. Ще получите имейл, когато бъде одобрен.' 
      };
    }

    const user: AuthUser = {
      id: authUser.id,
      email: authUser.email!,
      role: profile.role,
      profile: {
        id: profile.id,
        role: profile.role,
        status: profile.status,
        firstName: profile.full_name.split(' ')[0] || '',
        lastName: profile.full_name.split(' ').slice(1).join(' ') || '',
        email: profile.email,
        phone: profile.phone,
        companyName: profile.company,
        companyBulstat: profile.company_bulstat,
        skills: profile.skills,
        bio: profile.bio, // Using bio field for bio
        portfolioUrl: profile.portfolio_url,
        hourlyRate: profile.hourly_rate,
        createdAt: profile.created_at,
        updatedAt: profile.updated_at,
      },
    };

    return { success: true, user };
  } catch (error) {
    console.error('Get current user error:', error);
    return { success: false, error: 'Неочаквана грешка' };
  }
}

